/* Product Studio recommendations: native section rendering, scoped to each block. */
(() => {
  'use strict';
  if (window.SekageComplementaryProducts) {
    window.SekageComplementaryProducts.init(document);
    return;
  }

  const selector = '[data-sk-complementary-products]';
  const instances = new Map();
  const messages = {
    loading: 'Loading complementary products…',
    empty: 'No complementary products are available. Choose products in this block or configure complementary products in Search & Discovery.',
    error: 'Complementary products could not load. Select this block to retry, or choose products in its settings.',
  };

  class Recommendations {
    constructor(element) {
      this.element = element;
      this.content = element.querySelector('[data-sk-complementary-content]');
      this.status = element.querySelector('[data-sk-complementary-status]');
      this.editor = element.dataset.skEditor === 'true' || Boolean(window.Shopify?.designMode);
      this.controller = null;
      this.timeout = null;
      this.slider = null;
      this.destroyed = false;
      this.onImageLoad = () => this.slider?.resize();
      this.content?.addEventListener('rimg:load', this.onImageLoad);
      if (this.content?.querySelector('.complementary-product')) {
        this.setState('ready');
        this.setupSlider();
      } else if (element.dataset.skSource === 'custom' || element.dataset.skState === 'empty') {
        this.setState('empty');
      } else {
        this.load();
      }
    }

    setState(state) {
      if (this.destroyed) return;
      this.element.dataset.skState = state;
      this.element.hidden = state !== 'ready' && !this.editor;
      this.element.setAttribute('aria-busy', String(state === 'loading'));
      if (this.status) {
        this.status.hidden = state === 'ready';
        if (messages[state]) this.status.textContent = messages[state];
      }
      this.element.dispatchEvent(new CustomEvent('sekage:block:content', { bubbles: true }));
    }

    async load() {
      if (this.destroyed || this.controller || !this.content) return;
      const { skSectionId, skProductId, skRecommendationsUrl, skBlockId } = this.element.dataset;
      if (!skSectionId || !skProductId || !skRecommendationsUrl) {
        this.setState('error');
        return;
      }
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.setState('loading');
      this.timeout = setTimeout(() => {
        this.setState('error');
        this.controller?.abort();
      }, 15000);
      try {
        const url = new URL(skRecommendationsUrl, window.location.origin);
        url.searchParams.set('section_id', skSectionId);
        url.searchParams.set('product_id', skProductId);
        url.searchParams.set('limit', String(Math.min(10, Math.max(1, Number(this.element.dataset.skLimit) || 3))));
        url.searchParams.set('intent', 'complementary');
        const response = await fetch(url, { credentials: 'same-origin', signal });
        if (!response.ok) throw new Error(`Recommendations returned ${response.status}`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const source = Array.from(html.querySelectorAll(selector)).find(node => node.dataset.skBlockId === skBlockId && node.dataset.skSectionId === skSectionId);
        const content = source?.querySelector('[data-sk-complementary-content]');
        if (!content) throw new Error('Recommendation block missing from section response');
        if (this.destroyed || signal.aborted || !this.element.isConnected) return;
        this.content.innerHTML = content.innerHTML;
        if (!this.content.querySelector('.complementary-product')) {
          this.setState('empty');
          return;
        }
        this.setState('ready');
        // Turbo exposes these methods on window.rimg.shopify. Image enhancement
        // failure must not hide otherwise valid recommendation cards.
        try { window.rimg?.shopify?.watch?.(this.content); } catch { /* Cards remain usable. */ }
        await this.setupSlider();
      } catch (error) {
        if (error.name !== 'AbortError' && !this.destroyed) this.setState('error');
      } finally {
        clearTimeout(this.timeout);
        this.timeout = null;
        this.controller = null;
      }
    }

    async setupSlider() {
      const element = this.content?.querySelector('[data-sk-complementary-slider]');
      const slides = element?.querySelectorAll('[data-sk-complementary-slide]');
      if (!element || !slides || slides.length < 2 || this.slider) return;
      try {
        if (typeof window.loadFlickity === 'function') await window.loadFlickity();
        if (this.destroyed || !this.element.isConnected || !window.Flickity) return;
        slides.forEach(slide => slide.setAttribute('data-slide', ''));
        element.classList.remove('complementary-products__grid');
        this.slider = new window.Flickity(element, {
          cellSelector: '[data-sk-complementary-slide]',
          accessibility: true,
          adaptiveHeight: true,
          autoPlay: false,
          cellAlign: 'left',
          contain: true,
          imagesLoaded: true,
          pageDots: true,
          wrapAround: true,
          arrowShape: 'M 10,50 L 60,100 L 70,90 L 30,50 L 70,10 L 60,0 Z',
        });
      } catch {
        // Keep all cards usable as a plain list if the optional carousel fails.
        slides.forEach(slide => slide.removeAttribute('data-slide'));
        element.classList.add('complementary-products__grid');
      }
    }

    destroy() {
      this.destroyed = true;
      clearTimeout(this.timeout);
      this.controller?.abort();
      this.content?.removeEventListener('rimg:load', this.onImageLoad);
      this.slider?.destroy();
      if (this.content) {
        try { window.rimg?.shopify?.unwatch?.(this.content); } catch { /* Best-effort cleanup. */ }
      }
    }
  }

  const within = (scope, element) => scope === element || scope.contains(element);
  function init(scope) {
    for (const [element, instance] of instances) {
      if (!element.isConnected) {
        instance.destroy();
        instances.delete(element);
      }
    }
    const elements = scope.matches?.(selector) ? [scope] : Array.from(scope.querySelectorAll(selector));
    elements.forEach(element => {
      if (!instances.has(element)) instances.set(element, new Recommendations(element));
    });
  }
  function destroy(scope) {
    for (const [element, instance] of instances) {
      if (within(scope, element)) {
        instance.destroy();
        instances.delete(element);
      }
    }
  }

  window.SekageComplementaryProducts = { init, destroy };
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => destroy(event.target));
  document.addEventListener('shopify:block:select', event => {
    init(event.target);
    const element = event.target.matches?.(selector) ? event.target : event.target.querySelector(selector);
    const instance = instances.get(element);
    if (instance?.element.dataset.skState === 'error') instance.load();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
})();
